/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import useAuth from "$hooks/useAuth";
import useCompany from "$hooks/useCompany";
import useCompanyForm from "$hooks/useCompanyForm";
import useDockyardForm from "$hooks/useDockyardForm";
import useUserForm from "$hooks/useUserForm";
import useWarehouseForm from "$hooks/useWarehouseForm";
import { CompanyServices, DockyardServices, EntrepotService } from "$services";
import {
  CompanyType,
  DispositionAddressType,
  DispositionType,
  Warehouse,
} from "$types";
import { queryClient } from "$utils/queryClient";
import { normalizeName } from "$utils/utils";

const { ON_DOCKYARD, OTHER_PLACE, SUBCONTRACTED_WAREHOUSE, COLLECTED } =
  DispositionType;

type DeliveryDataType = {
  otherPlace: {
    dispositionPlace: string;
    dispositionAddress: DispositionAddressType;
  };
  subcontratedWarehouse: {
    entrepotName: string;
    dispositionAddress: DispositionAddressType;
  };
  collected: {
    entrepotName: string;
    dispositionAddress: DispositionAddressType;
  };
};

interface UseDeliveryEDBLogicProps {
  edbCompanyId: string | null;
}

export const useDeliveryEDBLogic = ({
  edbCompanyId,
}: UseDeliveryEDBLogicProps) => {
  const {
    formState: { errors },
    trigger,
    watch,
    clearErrors,
    setValue,
    getValues,
  } = useFormContext();

  const { isAdmin, isClient, user } = useAuth();
  const userCompanyId = user?.userInfo?.userCompany?._id;
  const { isClient: isClientCompany } = useCompany();
  const [companies, setCompanies] = useState([]);

  const intervenantCompany = watch("intervenantCompany", null);
  const dispositionType = watch("dispositionType", null);
  const currentDockyard = watch("dockyard", null);
  const dispositionDateIsExact = watch("dispositionDateIsExact", null);
  const country = watch("dispositionCountry", "France");
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  // One Dockyard
  const { data: dockyard } = useQuery({
    queryKey: ["getDockyardById", currentDockyard],
    queryFn: () => DockyardServices.getDockyardById(currentDockyard),
    enabled: !!currentDockyard,
    staleTime: 1000 * 60,
  });

  const isIntervenantCompanyClient = useMemo(() => {
    const currentIntervenantCompany = companies?.find(
      ({ _id }) => _id === intervenantCompany
    );
    const result = currentIntervenantCompany
      ? isClientCompany(currentIntervenantCompany?.companyTypes)
      : false;
    return result;
  }, [companies, intervenantCompany, isClientCompany]);

  const isOnDockyard = dispositionType === ON_DOCKYARD;

  const defaultDeliveryData: DeliveryDataType = {
    otherPlace: {
      dispositionPlace: getValues("dispositionPlace") ?? "",
      dispositionAddress: {
        dispositionAdress: getValues("dispositionAdress") ?? "",
        dispositionAdress2: getValues("dispositionAdress2") ?? "",
        dispositionPostalCode: getValues("dispositionPostalCode") ?? "",
        dispositionCity: getValues("dispositionCity") ?? "",
        dispositionCountry: getValues("dispositionCountry") ?? "",
      },
    },
    subcontratedWarehouse: {
      entrepotName: getValues("entrepotName") ?? "",
      dispositionAddress: {
        dispositionAdress: getValues("dispositionAdress") ?? "",
        dispositionAdress2: getValues("dispositionAdress2") ?? "",
        dispositionPostalCode: getValues("dispositionPostalCode") ?? "",
        dispositionCity: getValues("dispositionCity") ?? "",
        dispositionCountry: getValues("dispositionCountry") ?? "",
      },
    },
    collected: {
      entrepotName: getValues("entrepotName") ?? "",
      dispositionAddress: {
        dispositionAdress: getValues("dispositionAdress") ?? "",
        dispositionAdress2: getValues("dispositionAdress2") ?? "",
        dispositionPostalCode: getValues("dispositionPostalCode") ?? "",
        dispositionCity: getValues("dispositionCity") ?? "",
        dispositionCountry: getValues("dispositionCountry") ?? "",
      },
    },
  };

  const [deliveryData, setDeliveryData] =
    useState<DeliveryDataType>(defaultDeliveryData);

  const setDeliveryDataDispositionAddress = (
    deliveryAddress: DispositionAddressType
  ) => {
    switch (dispositionType) {
      case OTHER_PLACE:
        setDeliveryData({
          ...deliveryData,
          otherPlace: {
            ...deliveryData.otherPlace,
            dispositionAddress: {
              ...deliveryData.otherPlace.dispositionAddress,
              ...deliveryAddress,
            },
          },
        });
        break;
      case SUBCONTRACTED_WAREHOUSE:
        setDeliveryData({
          ...deliveryData,
          subcontratedWarehouse: {
            ...deliveryData.subcontratedWarehouse,
            dispositionAddress: {
              ...deliveryData.subcontratedWarehouse.dispositionAddress,
              ...deliveryAddress,
            },
          },
        });
        break;
      case COLLECTED:
        setDeliveryData({
          ...deliveryData,
          collected: {
            ...deliveryData.collected,
            dispositionAddress: {
              ...deliveryData.collected.dispositionAddress,
              ...deliveryAddress,
            },
          },
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    switch (dispositionType) {
      case OTHER_PLACE:
        setValue("dispositionPlace", deliveryData.otherPlace.dispositionPlace);
        setValue(
          "dispositionAdress",
          deliveryData.otherPlace.dispositionAddress.dispositionAdress
        );
        setValue(
          "dispositionAdress2",
          deliveryData.otherPlace.dispositionAddress.dispositionAdress2
        );
        setValue(
          "dispositionPostalCode",
          deliveryData.otherPlace.dispositionAddress.dispositionPostalCode
        );
        setValue(
          "dispositionCity",
          deliveryData.otherPlace.dispositionAddress.dispositionCity
        );
        setValue(
          "dispositionCountry",
          deliveryData.otherPlace.dispositionAddress.dispositionCountry
        );
        break;
      case SUBCONTRACTED_WAREHOUSE:
        setValue(
          "entrepotName",
          deliveryData.subcontratedWarehouse.entrepotName
        );
        setValue(
          "dispositionAdress",
          deliveryData.subcontratedWarehouse.dispositionAddress
            .dispositionAdress
        );
        setValue(
          "dispositionAdress2",
          deliveryData.subcontratedWarehouse.dispositionAddress
            .dispositionAdress2
        );
        setValue(
          "dispositionPostalCode",
          deliveryData.subcontratedWarehouse.dispositionAddress
            .dispositionPostalCode
        );
        setValue(
          "dispositionCity",
          deliveryData.subcontratedWarehouse.dispositionAddress.dispositionCity
        );
        setValue(
          "dispositionCountry",
          deliveryData.subcontratedWarehouse.dispositionAddress
            .dispositionCountry
        );
        break;
      case COLLECTED:
        setValue("entrepotName", deliveryData.collected.entrepotName);
        setValue(
          "dispositionAdress",
          deliveryData.collected.dispositionAddress.dispositionAdress
        );
        setValue(
          "dispositionAdress2",
          deliveryData.collected.dispositionAddress.dispositionAdress2
        );
        setValue(
          "dispositionPostalCode",
          deliveryData.collected.dispositionAddress.dispositionPostalCode
        );
        setValue(
          "dispositionCity",
          deliveryData.collected.dispositionAddress.dispositionCity
        );
        setValue(
          "dispositionCountry",
          deliveryData.collected.dispositionAddress.dispositionCountry
        );
        break;
      default:
        break;
    }
  }, [dispositionType]);

  const {
    togglePopin: toggleWarehousePopin,
    handleSubmit: onSubmitWarehouse,
    isOpened: isWarehousePopinOpened,
    openPopin: openWarehousePopin,
    error,
    submitting: warehouseSubmitting,
  } = useWarehouseForm({});

  const {
    handleSubmit: handleSubmitIntervenant,
    togglePopin: toggleIntervenantPopin,
    isOpened: isIntervenantOpened,
    openPopin: openIntervenantPopin,
    error: companyError,
    submitting: intervenantSubmitting,
  } = useCompanyForm();

  const {
    open: openNewContactPopin,
    togglePopin: toggleNewContactDrawer,
    onSubmit: onSubmitContact,
    error: errorUser,
  } = useUserForm(intervenantCompany);

  const setValueAndValidate = useCallback(
    (name: string, value: any) => {
      setValue(name, value, { shouldValidate: true });
    },
    [setValue]
  );

  const handleSubmitContact = async (data: any) => {
    const contact = await onSubmitContact(data);
    setContactForm(contact);
  };

  const setDispositionAdressFromDockyard = useCallback(() => {
    if (dockyard?.address) {
      setValueAndValidate("dispositionAdress", dockyard.address);
      setValueAndValidate("dispositionAdress2", dockyard.address2);
      setValueAndValidate("dispositionPostalCode", dockyard.postalCode);
      setValueAndValidate("dispositionCity", dockyard.city);
      setValueAndValidate("dispositionCountry", dockyard.country);
    }
  }, [
    dockyard?.address,
    dockyard?.address2,
    dockyard?.city,
    dockyard?.country,
    dockyard?.postalCode,
    setValueAndValidate,
  ]);

  const setContactForm = (contact: any) => {
    setValueAndValidate("intervenantContact", contact?._id);
    setValueAndValidate("firstnameContactRecovery", contact?.firstname);
    setValueAndValidate(
      "lastnameContactRecovery",
      normalizeName(contact?.lastname)
    );
    setValueAndValidate("emailContactRecovery", contact?.email);
    setValueAndValidate("phoneContactRecovery", contact?.phone);
  };

  const handleDispositionType = (val) => {
    setValueAndValidate("dispositionType", val);
    setValueAndValidate("entrepotName", null);
    if (val === ON_DOCKYARD) {
      setDispositionAdressFromDockyard();
      return;
    }
    // Clear the address fields for other disposition types
    setValue("dispositionAdress", "");
    setValue("dispositionAdress2", "");
    setValue("dispositionPostalCode", "");
    setValue("dispositionCity", "");
    setValue("dispositionCountry", "");
  };

  const handleSubmitWarehouse = async (data: any) => {
    const warehouse = await onSubmitWarehouse(data);
    setValueAndValidate("entrepotName", warehouse.name);
    onWarehouseChange(warehouse);
    trigger();
  };

  const onWarehouseChange = useCallback(
    (warehouse) => {
      setValueAndValidate(
        "dispositionAdress",
        warehouse?.location.adress || ""
      );
      setValueAndValidate(
        "dispositionAdress2",
        warehouse?.location.complement || ""
      );
      setValueAndValidate(
        "dispositionPostalCode",
        warehouse?.location.cp || ""
      );
      setValueAndValidate("dispositionCity", warehouse?.location.city || "");
      setValueAndValidate(
        "dispositionCountry",
        warehouse?.location.country || "France"
      );
      const addressType =
        dispositionType === COLLECTED ? "collected" : "subcontratedWarehouse";
      setDeliveryData((prevDeliveryData) => {
        return {
          ...prevDeliveryData,
          [addressType]: {
            ...prevDeliveryData[addressType],
            entrepotName: warehouse?.name,
            dispositionAddress: {
              dispositionAdress: warehouse?.location.adress ?? "",
              dispositionAdress2: warehouse?.location.complement ?? "",
              dispositionPostalCode: warehouse?.location.cp ?? "",
              dispositionCity: warehouse?.location.city ?? "",
              dispositionCountry: warehouse?.location.country ?? "France",
            },
          },
        };
      });
    },
    [setValueAndValidate, dispositionType]
  );

  const {
    classes: useDockyardClasses,
    togglePopin,
    handleSubmit: handleSubmitDockyard,
    isOpened: isDockyardPopinOpened,
    error: errorDockyard,
    submitting,
  } = useDockyardForm();

  const intervenantOptions = useMemo(() => {
    const interveningCompanyArray = [
      ...new Set(dockyard?.interveningCompany?.map((company) => company._id)),
    ];
    return companies?.filter((c) => {
      const isSelfCompany = c._id === userCompanyId;
      const isEdbCompany = c._id === edbCompanyId;
      const isDockyardCompany = c._id === dockyard?.company?._id;
      const isDockyardInterveningCompany = interveningCompanyArray.includes(
        c._id
      );
      const isContributorOfEdbCompany = c.contributorOf?.includes(edbCompanyId);
      const isLogistician = c.companyTypes?.includes(CompanyType.LOGISTICIAN);

      return (
        isSelfCompany ||
        isEdbCompany ||
        isDockyardInterveningCompany ||
        isDockyardCompany ||
        isLogistician ||
        ((isAdmin() || isClient()) && isContributorOfEdbCompany)
      );
    });
  }, [
    companies,
    dockyard?.company,
    dockyard?.interveningCompany,
    edbCompanyId,
    isAdmin,
    isClient,
    userCompanyId,
  ]);

  useEffect(() => {
    if (
      isOnDockyard &&
      dockyard &&
      (!dockyard?.address ||
        !dockyard?.postalCode ||
        !dockyard?.city ||
        !dockyard?.country)
    ) {
      setValueAndValidate("dispositionAdress", dockyard?.address);
    }
  }, [dispositionType, dockyard, isOnDockyard]);

  useEffect(() => {
    if (!isOnDockyard) {
      clearErrors("dispositionAdress");
    }
  }, [clearErrors, isOnDockyard]);

  const displayAdressBlock =
    dispositionType === SUBCONTRACTED_WAREHOUSE ||
    dispositionType === OTHER_PLACE ||
    (isOnDockyard && !errors.dispositionAdress);
  const canCompleteAddress = isAdmin();

  useEffect(() => {
    if (dockyard && isOnDockyard) {
      setDispositionAdressFromDockyard();
    }
  }, [
    clearErrors,
    dockyard,
    isOnDockyard,
    displayAdressBlock,
    setDispositionAdressFromDockyard,
    setValue,
  ]);

  const fetchCompanies = useCallback(async () => {
    const filters = {
      isActive: true,
      page: 1,
      rowsPerPage: 50000,
    };
    let companies;
    companies = await queryClient.getQueryData(["getCompanies", filters]);
    if (!companies) {
      companies = await queryClient.fetchQuery({
        queryKey: ["getCompanies", filters],
        queryFn: () => CompanyServices.getCompanies(filters),
        staleTime: 60 * 1000,
      });
      companies = companies.data;
      await queryClient.setQueryData(["getCompanies", filters], companies);
    }
    setCompanies(companies);
  }, [setCompanies]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const fetchContributorWarehouses = useCallback(
    async (companyId: string) => {
      if (!companyId) return;

      const filters = { company: companyId };
      const queryKey = ["getEntrepots", filters];
      let getWarehouses: any = await queryClient.getQueryData(queryKey);

      if (!getWarehouses) {
        getWarehouses = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => EntrepotService.getEntrepots(filters),
          gcTime: 5 * 60 * 1000,
        });
        queryClient.setQueryData(queryKey, getWarehouses);
      }

      // Instead of replacing warehouses, merge them with existing ones
      setWarehouses((prevWarehouses) => {
        const newWarehouses = getWarehouses?.data || [];
        const mergedWarehouses = [...prevWarehouses];

        // Add new warehouses that aren't already in the list
        newWarehouses.forEach((newWarehouse) => {
          if (!mergedWarehouses.some((w) => w._id === newWarehouse._id)) {
            mergedWarehouses.push(newWarehouse);
          }
        });

        return mergedWarehouses;
      });

      // Only set initial warehouse if specifically requested
      // if (
      //   dispositionType === SUBCONTRACTED_WAREHOUSE &&
      //   getWarehouses?.data?.length > 0 &&
      //   companyId === intervenantCompany
      // ) {
      //   const warehouses =
      //     getWarehouses?.data.filter((warehouse) => {
      //       return (
      //         warehouse.company?._id === intervenantCompany &&
      //         (warehouse.companies?.some(
      //           (c) => c._id === dockyard?.company?._id
      //         ) ||
      //           dockyard.company._id === warehouse.company?._id)
      //       );
      //     }) || [];
      //   const lastWarehouse = warehouses[warehouses.length - 1];
      //   onWarehouseChange(lastWarehouse);
      //   setValue("entrepotName", lastWarehouse.name);
      // }
    },
    [intervenantCompany, onWarehouseChange, setValue, dispositionType]
  );

  // Add this new effect after the existing useEffects
  useEffect(() => {
    const fetchAllWarehouses = async () => {
      if (!dockyard?.company?._id) return;

      // Clear warehouses when dockyard changes
      setWarehouses([]);

      // Fetch client company warehouses
      await fetchContributorWarehouses(dockyard.company._id);

      // Fetch warehouses for all intervening companies
      if (dockyard.interveningCompany) {
        await Promise.all(
          dockyard.interveningCompany.map((company) =>
            fetchContributorWarehouses(company._id)
          )
        );
      }
    };

    fetchAllWarehouses();
  }, [dockyard?.company?._id, dockyard?.interveningCompany]);

  // Keep the existing effect for when intervenant company changes
  useEffect(() => {
    if (
      (dispositionType === SUBCONTRACTED_WAREHOUSE ||
        dispositionType === COLLECTED) &&
      intervenantCompany
    ) {
      fetchContributorWarehouses(intervenantCompany);
    }
  }, [
    companies,
    dispositionType,
    intervenantCompany,
    fetchContributorWarehouses,
    isAdmin,
    onWarehouseChange,
    setValue,
  ]);

  useEffect(() => {
    if (dockyard?.company?._id) {
      fetchContributorWarehouses(dockyard.company._id);
    }
  }, [fetchContributorWarehouses, dockyard?.company?._id]);

  return {
    dispositionDateIsExact,
    edbCompanyId,
    canCompleteAddress,
    displayAdressBlock,
    intervenantOptions,
    useDockyardClasses,
    togglePopin,
    handleSubmitDockyard,
    isDockyardPopinOpened,
    errorDockyard,
    submitting,
    onWarehouseChange,
    handleSubmitWarehouse,
    handleDispositionType,
    setContactForm,
    handleSubmitContact,
    setValueAndValidate,
    openNewContactPopin,
    toggleNewContactDrawer,
    errorUser,
    handleSubmitIntervenant,
    toggleIntervenantPopin,
    isIntervenantOpened,
    openIntervenantPopin,
    companyError,
    intervenantSubmitting,
    toggleWarehousePopin,
    isWarehousePopinOpened,
    openWarehousePopin,
    error,
    warehouseSubmitting,
    setDeliveryDataDispositionAddress,
    deliveryData,
    setDeliveryData,
    isOnDockyard,
    isIntervenantCompanyClient,
    dockyard,
    warehouses,
    country,
    dispositionType,
    intervenantCompany,
  };
};
